// PlayFirst Parent — Activity feedback dialog. Opens from the Today's Play
// Idea card. Captures:
//   * Was this useful? (thumbs)
//   * Difficulty (too easy / just right / too hard) — feeds the next AI prompt
//   * Engagement (not interested / curious / engaged / loved it)
//   * Notes (new words, observations) — saved to child's record
//   * Mark as complete
// All fields are optional; the parent can save partial feedback.
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import {
  ThumbsUp, ThumbsDown, Smile, Loader2, Check, Sparkles,
} from 'lucide-react';
import { PFP } from './styles';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';
const API = `${BACKEND_URL}/api`;

const DIFFICULTY = [
  { v: 'too_easy', l: 'Too easy', emoji: '😌' },
  { v: 'just_right', l: 'Just right', emoji: '🌱' },
  { v: 'too_hard', l: 'Too hard', emoji: '😅' },
];

const ENGAGEMENT = [
  { v: 'not_interested', l: 'Not interested', emoji: '😐' },
  { v: 'curious', l: 'Curious', emoji: '🤔' },
  { v: 'engaged', l: 'Engaged', emoji: '😊' },
  { v: 'loved_it', l: 'Loved it', emoji: '🤩' },
];

function PillBtn({ active, onClick, children, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`${PFP.chip} ${active ? PFP.chipActive : PFP.chipIdle}`}
    >
      {children}
    </button>
  );
}

export default function ActivityFeedbackDialog({
  open,
  onOpenChange,
  playIdea,            // current play idea doc
  existingFeedback,    // previously-saved feedback for this idea (or null)
  onSaved,
}) {
  const [useful, setUseful] = useState(null);
  const [difficulty, setDifficulty] = useState('');
  const [engagement, setEngagement] = useState('');
  const [notes, setNotes] = useState('');
  const [newWords, setNewWords] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    setUseful(existingFeedback?.useful ?? null);
    setDifficulty(existingFeedback?.difficulty || '');
    setEngagement(existingFeedback?.engagement || '');
    setNotes(existingFeedback?.notes || '');
    setNewWords((existingFeedback?.new_words || []).join(', '));
  }, [open, existingFeedback]);

  const submit = async (markComplete) => {
    if (!playIdea?.id) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...(useful !== null && { useful }),
        ...(difficulty && { difficulty }),
        ...(engagement && { engagement }),
        ...(notes && { notes: notes.trim() }),
        new_words: newWords
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean),
        ...(markComplete && { completed: true }),
      };
      const res = await axios.post(
        `${API}/parent-app/play-ideas/${playIdea.id}/feedback`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      toast.success(markComplete ? 'Logged and marked complete' : 'Feedback saved');
      onSaved && onSaved(res.data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="rounded-3xl max-w-lg max-h-[90vh] overflow-y-auto"
        data-testid="activity-feedback-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#3a2f4f]">
            How did it go?
          </DialogTitle>
          <DialogDescription>
            Your feedback shapes tomorrow's play idea. There's no wrong answer.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 mt-2">
          {/* Useful */}
          <div>
            <p className="text-sm text-stone-600 mb-2">Was this idea useful?</p>
            <div className="flex gap-2">
              <PillBtn
                active={useful === true}
                onClick={() => setUseful(useful === true ? null : true)}
                testid="feedback-useful-yes"
              >
                <ThumbsUp className="w-4 h-4 mr-1.5 inline" /> Yes
              </PillBtn>
              <PillBtn
                active={useful === false}
                onClick={() => setUseful(useful === false ? null : false)}
                testid="feedback-useful-no"
              >
                <ThumbsDown className="w-4 h-4 mr-1.5 inline" /> Not really
              </PillBtn>
            </div>
          </div>

          {/* Difficulty */}
          <div>
            <p className="text-sm text-stone-600 mb-2">Difficulty for your child</p>
            <div className="flex flex-wrap gap-2">
              {DIFFICULTY.map((d) => (
                <PillBtn
                  key={d.v}
                  active={difficulty === d.v}
                  onClick={() => setDifficulty(difficulty === d.v ? '' : d.v)}
                  testid={`feedback-difficulty-${d.v}`}
                >
                  <span className="mr-1.5">{d.emoji}</span>
                  {d.l}
                </PillBtn>
              ))}
            </div>
          </div>

          {/* Engagement */}
          <div>
            <p className="text-sm text-stone-600 mb-2">How engaged were they?</p>
            <div className="flex flex-wrap gap-2">
              {ENGAGEMENT.map((d) => (
                <PillBtn
                  key={d.v}
                  active={engagement === d.v}
                  onClick={() => setEngagement(engagement === d.v ? '' : d.v)}
                  testid={`feedback-engagement-${d.v}`}
                >
                  <span className="mr-1.5">{d.emoji}</span>
                  {d.l}
                </PillBtn>
              ))}
            </div>
          </div>

          {/* New words / phrases */}
          <div>
            <label className="block text-sm text-stone-600 mb-1.5">
              New words, sounds or phrases you noticed (comma-separated)
            </label>
            <input
              type="text"
              value={newWords}
              onChange={(e) => setNewWords(e.target.value)}
              placeholder="vroom, more please, all done"
              className="w-full h-11 px-4 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="feedback-new-words"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm text-stone-600 mb-1.5">
              Other observations (optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="They pointed at the bubbles for the first time…"
              className="w-full px-4 py-3 min-h-24 rounded-2xl border border-stone-200 bg-white focus:border-[#7C6F9C] focus:ring-2 focus:ring-[#7C6F9C]/15 outline-none"
              data-testid="feedback-notes"
            />
          </div>

          <div className="flex items-center gap-2 text-xs text-[#7C6F9C]">
            <Sparkles className="w-3.5 h-3.5" />
            We use these signals to shape your next play idea.
          </div>
        </div>

        <DialogFooter className="gap-2 mt-2">
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className={PFP.btnGhost}
            data-testid="feedback-cancel-btn"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => submit(false)}
            disabled={saving}
            className="rounded-full px-5 py-2.5 border border-[#7C6F9C]/30 text-[#7C6F9C] hover:bg-[#7C6F9C]/10 inline-flex items-center gap-2"
            data-testid="feedback-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            Save
          </button>
          <button
            type="button"
            onClick={() => submit(true)}
            disabled={saving}
            className={PFP.btnPrimary + ' inline-flex items-center gap-2'}
            data-testid="feedback-mark-complete-btn"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            Mark complete
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
